import { prefersReducedMotion } from './motion';

/**
 * Testimonials enhancement. Two independent pieces of progressive
 * enhancement, each guarding its own prerequisites:
 *
 *  - initTestimonialCarousel wires the horizontally scrollable card strip
 *    to prev/next buttons and a dot indicator. Without JS the strip is a
 *    native scroll-snap scroller with a visible scrollbar, so every card
 *    stays reachable.
 *  - initTestimonialDialog wires the "read full testimonial" buttons to a
 *    shared native <dialog>, which gives focus trapping, Escape-to-close,
 *    and a backdrop for free. Without JS those buttons are simply inert and
 *    the truncated excerpt remains fully readable.
 */
export function initTestimonials(): void {
  initTestimonialCarousel();
  initTestimonialDialog();
}

function initTestimonialCarousel(): void {
  const carousel = document.querySelector<HTMLElement>('[data-testimonial-carousel]');
  if (!carousel) return;

  const track = carousel.querySelector<HTMLElement>('[data-carousel-track]');
  const dotsContainer = carousel.querySelector<HTMLElement>('[data-carousel-dots]');
  const prevBtn = carousel.querySelector<HTMLButtonElement>('[data-carousel-prev]');
  const nextBtn = carousel.querySelector<HTMLButtonElement>('[data-carousel-next]');
  if (!track || !dotsContainer || !prevBtn || !nextBtn) return;

  const slides = Array.from(track.querySelectorAll<HTMLElement>('.testimonial-carousel__slide'));
  if (slides.length === 0) return;

  // One scroll stop per distinct left-aligned position. When several cards
  // are visible at once, the trailing cards that can never reach the left
  // edge collapse onto the single max-scroll stop, so the dot count always
  // matches the number of reachable positions (no phantom dots).
  let stops: number[] = [];
  let dots: HTMLButtonElement[] = [];
  let activeIndex = 0;

  const behavior = (): ScrollBehavior => (prefersReducedMotion() ? 'auto' : 'smooth');

  const computeStops = (): number[] => {
    const base = slides[0].offsetLeft;
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const seen = new Set<number>();
    const result: number[] = [];
    for (const slide of slides) {
      const pos = Math.min(Math.max(0, slide.offsetLeft - base), maxScroll);
      const key = Math.round(pos);
      if (!seen.has(key)) {
        seen.add(key);
        result.push(pos);
      }
    }
    return result;
  };

  const goTo = (index: number): void => {
    const clamped = Math.max(0, Math.min(index, stops.length - 1));
    track.scrollTo({ left: stops[clamped], behavior: behavior() });
  };

  const buildDots = (): void => {
    dotsContainer.replaceChildren();
    dots = stops.map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'testimonial-carousel__dot';
      dot.setAttribute('aria-label', `Show testimonials, position ${i + 1} of ${stops.length}`);
      dot.addEventListener('click', () => goTo(i));
      dotsContainer.appendChild(dot);
      return dot;
    });
  };

  const nearestIndex = (): number => {
    const x = track.scrollLeft;
    let best = 0;
    let bestDist = Infinity;
    stops.forEach((pos, i) => {
      const dist = Math.abs(pos - x);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    return best;
  };

  const syncUi = (): void => {
    activeIndex = nearestIndex();
    dots.forEach((dot, i) => {
      const isActive = i === activeIndex;
      dot.classList.toggle('is-active', isActive);
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    prevBtn.disabled = track.scrollLeft <= 1;
    nextBtn.disabled = track.scrollLeft >= maxScroll - 1;
  };

  const rebuild = (): void => {
    const previous = stops.length;
    stops = computeStops();
    if (stops.length !== previous) buildDots();
    carousel.classList.toggle('is-single', stops.length <= 1);
    syncUi();
  };

  prevBtn.addEventListener('click', () => goTo(activeIndex - 1));
  nextBtn.addEventListener('click', () => goTo(activeIndex + 1));

  let scrollRaf = 0;
  track.addEventListener('scroll', () => {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0;
      syncUi();
    });
  });

  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(rebuild, 150);
  });

  carousel.classList.add('is-enhanced');
  rebuild();

  // Web fonts can shift card widths after our first measurement; remeasure
  // once they settle so the stops and dots stay accurate.
  document.fonts?.ready.then(rebuild).catch(() => {});
}

function initTestimonialDialog(): void {
  const dialog = document.getElementById('testimonial-dialog') as HTMLDialogElement | null;
  if (!dialog || typeof dialog.showModal !== 'function') return;

  const titleEl = dialog.querySelector<HTMLElement>('[data-testimonial-title]');
  const bodyEl = dialog.querySelector<HTMLElement>('[data-testimonial-body]');
  const nameEl = dialog.querySelector<HTMLElement>('[data-testimonial-name]');
  const closeBtn = dialog.querySelector<HTMLButtonElement>('[data-testimonial-close]');
  const triggers = Array.from(
    document.querySelectorAll<HTMLButtonElement>('[data-testimonial-trigger]')
  );
  if (!titleEl || !bodyEl || !nameEl || triggers.length === 0) return;

  let lastFocused: HTMLElement | null = null;

  const open = (trigger: HTMLButtonElement) => {
    const template = trigger
      .closest('blockquote')
      ?.querySelector<HTMLTemplateElement>('template[data-testimonial-content]');
    if (!template) return;

    titleEl.textContent = trigger.dataset.testimonialTitle ?? '';
    nameEl.textContent = trigger.dataset.testimonialName ?? '';
    bodyEl.replaceChildren(template.content.cloneNode(true));

    lastFocused = document.activeElement as HTMLElement;
    dialog.showModal();
    closeBtn?.focus();
  };

  triggers.forEach((trigger) => trigger.addEventListener('click', () => open(trigger)));

  closeBtn?.addEventListener('click', () => dialog.close());

  // Clicking the backdrop (the dialog element itself, outside the panel) closes it.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  dialog.addEventListener('close', () => {
    lastFocused?.focus();
  });
}
